import { useCallback, useMemo } from "react";

import { useUsersCount } from "@/common/data/useUsers";
import { useDelayedTrue } from "@/common/hooks/useDelayedTrue";
import type { SegmentViewType } from "@/company/components/SegmentViewToggle";
import { useCompaniesCount } from "@/company/data/useCompaniesData";
import type { UIFilter } from "@reflag/shared/filter";

const STALE_COUNT_HIDE_DELAY_MS = 300;

export type SegmentAudienceCountStatus =
  | "loading"
  | "ready"
  | "unavailable"
  | "error";

export type SegmentAudienceCountState = {
  displayedTotalCount?: number;
  refetch: () => void;
  status: SegmentAudienceCountStatus;
};

export function useSegmentAudienceCount({
  viewType,
  filter,
  companySearchQuery,
  userSearchQuery,
}: {
  viewType: SegmentViewType;
  filter: UIFilter;
  companySearchQuery: string;
  userSearchQuery: string;
}): SegmentAudienceCountState {
  const isCompaniesView = viewType === "companies";

  const companiesCountQuery = useCompaniesCount(
    {
      filter,
      idNameFilter: companySearchQuery,
    },
    isCompaniesView,
  );
  const usersCountQuery = useUsersCount(
    {
      filter,
      idNameEmailFilter: userSearchQuery,
    },
    { enabled: !isCompaniesView },
  );

  const activeCountQuery = isCompaniesView
    ? companiesCountQuery
    : usersCountQuery;
  const totalCount = activeCountQuery.data?.totalCount;
  const hasUnavailableCount =
    !activeCountQuery.isPlaceholderData && totalCount === null;
  const shouldHidePlaceholderCount = useDelayedTrue(
    activeCountQuery.isPlaceholderData,
    STALE_COUNT_HIDE_DELAY_MS,
  );
  const displayedTotalCount =
    activeCountQuery.isError ||
    hasUnavailableCount ||
    shouldHidePlaceholderCount
      ? undefined
      : (totalCount ?? undefined);
  const status: SegmentAudienceCountStatus = activeCountQuery.isError
    ? "error"
    : hasUnavailableCount
      ? "unavailable"
      : displayedTotalCount === undefined
        ? "loading"
        : "ready";

  const refetchCompaniesCount = companiesCountQuery.refetch;
  const refetchUsersCount = usersCountQuery.refetch;
  const refetch = useCallback(() => {
    if (isCompaniesView) {
      void refetchCompaniesCount();
      return;
    }

    void refetchUsersCount();
  }, [isCompaniesView, refetchCompaniesCount, refetchUsersCount]);

  return useMemo(
    () => ({
      displayedTotalCount,
      refetch,
      status,
    }),
    [displayedTotalCount, refetch, status],
  );
}
